import React, { useCallback, useMemo } from 'react';
import * as S from './SearchSuggestions.styles';
import { SearchSuggestionsProps } from './SearchSuggestions.types';
import { useSearchStore } from '@/store/useSearchStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { cutSentenceFor2Parts } from '@/utils/helpers';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { SelectAngle } from '@/components/UI/Select/Select.styles';
import { AngleBackIcon } from '@/components/UI/Icons/Icons';

export const SearchSuggestions = ({
  searchQuery,
  suggestions,
  onSelect,
}: SearchSuggestionsProps) => {
  const isSuggestionsOpened = useSearchStore(
    state => state.isSuggestionsOpened
  );
  const { isDesktop } = useBreakpoint();

  const processedSuggestions = useMemo(
    () =>
      suggestions.map(suggestion => {
        const [entered, rest] = cutSentenceFor2Parts(
          suggestion.value,
          searchQuery
        );

        return { ...suggestion, entered, rest };
      }),
    [suggestions, searchQuery]
  );

  const getSuggestionText = useCallback(
    (entered: string, rest: string, hasTestId?: boolean) => {
      return (
        <S.SearchSuggestionText
          {...(hasTestId && {
            'data-testid': CommonTestIds.searchSuggestions.text,
          })}
        >
          {entered}
          <b>{rest}</b>
        </S.SearchSuggestionText>
      );
    },
    []
  );

  const angleIcon = useMemo(
    () => (
      <SelectAngle>
        <AngleBackIcon />
      </SelectAngle>
    ),
    []
  );

  if (!suggestions.length) return null;

  return (
    <S.SearchSuggestionsDropdown isOpen={isSuggestionsOpened}>
      <S.SearchSuggestionsContent
        data-testid={CommonTestIds.searchSuggestions.content}
      >
        {processedSuggestions.map((suggestion, i, arr) => {
          return (
            <S.SearchSuggestionItem
              key={`${suggestion.value}-${suggestion.subvalue}-${i}`}
              $isFirst={!i}
              $isLast={i === arr.length - 1}
              onClick={() => onSelect(suggestion)}
              data-testid={CommonTestIds.searchSuggestions.item(i)}
            >
              {suggestion.subvalue ? (
                <S.SearchSuggestionCategoryItem>
                  {getSuggestionText(suggestion.entered, suggestion.rest, true)}
                  <S.SearchSuggestionCategoryText
                    data-testid={CommonTestIds.searchSuggestions.category}
                  >
                    {suggestion.subvalue}
                  </S.SearchSuggestionCategoryText>
                </S.SearchSuggestionCategoryItem>
              ) : (
                getSuggestionText(suggestion.entered, suggestion.rest)
              )}

              {!isDesktop && angleIcon}
            </S.SearchSuggestionItem>
          );
        })}
      </S.SearchSuggestionsContent>
    </S.SearchSuggestionsDropdown>
  );
};
